import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import VimeoPlayer from "@vimeo/player";

/*
  Hero intro loop:
    1. INTRO   — sequentially reveal "DO" → "IT" → "WITH" → "LOVE!!"
    2. VIDEO   — fade the Vimeo background in, play 2 full loops
    3. FADE    — fade the video out to the white hero background
    4. repeat

  Self-contained: renders the iframe, overlay, and white-fade layer.
  Notifies the parent via `onActiveChange(true|false)` when the intro overlay
  is active so the parent can hide/show its CTA content.

  IMPORTANT: the cycle scheduler does NOT block on Vimeo Player promises
  (those can hang on slow networks / blocked iframes). Player operations
  are fire-and-forget; cycle timing is driven purely by setTimeout.
*/

const WORDS = [
  { text: "DO",   gold: false, hold: 620 },
  { text: "IT",   gold: false, hold: 620 },
  { text: "WITH", gold: false, hold: 620 },
  { text: "LOVE", gold: false, hold: 1100 },
];

const VIDEO_FADE_MS = 850;
const VIDEO_READY_TIMEOUT_MS = 4500;
const FALLBACK_DURATION_S = 15;

const VIMEO_SRC =
  "https://player.vimeo.com/video/1185986598?background=1&autoplay=1&loop=1&muted=1&controls=0&title=0&byline=0&portrait=0&transparent=0&dnt=1&playsinline=1";

export default function HeroIntroLoop({ onActiveChange }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const durationRef = useRef(FALLBACK_DURATION_S);

  const [phase, setPhase] = useState("intro"); // "intro" | "video"
  const [wordIdx, setWordIdx] = useState(0);
  const [videoVisible, setVideoVisible] = useState(false);
  // Flips to true only once the Vimeo iframe has actually rendered a frame
  // (fires on "playing" or first "timeupdate"). Until then we keep the
  // video layer at opacity 0 so no poster / loading card can ever flash.
  const [videoReady, setVideoReady] = useState(false);

  // Notify parent when intro is active.
  useEffect(() => {
    onActiveChange?.(phase === "intro");
  }, [phase, onActiveChange]);

  // ── Vimeo player setup (non-blocking, runs in parallel with the cycle) ──
  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      // Wait until iframe is mounted
      let tries = 0;
      while (!iframeRef.current && tries < 40 && !cancelled) {
        await new Promise((r) => setTimeout(r, 100));
        tries += 1;
      }
      if (cancelled || !iframeRef.current) return;

      try {
        const p = new VimeoPlayer(iframeRef.current);
        playerRef.current = p;

        // The moment the iframe actually renders a frame, mark ready.
        // Either event covers all autoplay paths (Chrome/Safari/Firefox).
        const markReady = () => { if (!cancelled) setVideoReady(true); };
        try { p.on("playing", markReady); } catch {}
        try { p.on("timeupdate", markReady); } catch {}

        await p.ready();
        if (cancelled) return;
        try { await p.setMuted(true); } catch {}
        try { await p.setVolume(0); } catch {}
        try {
          const d = await p.getDuration();
          if (d && d > 1) durationRef.current = d;
        } catch {}
        // NOTE: we do NOT pause here. Letting the video auto-play silently
        // behind the white intro overlay lets the first frame buffer up
        // before we ever reveal it, which eliminates the first-load glitch.
      } catch (e) {
        // graceful degrade — cycle still runs on fallback timing
      }
    };

    // Hard safety net: if the Vimeo events never fire (iframe blocked,
    // ad-blocker, etc.), unblock after a short wait so the site doesn't
    // sit on the white intro forever.
    const safety = setTimeout(() => {
      if (!cancelled) setVideoReady(true);
    }, VIDEO_READY_TIMEOUT_MS);

    setup();
    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
  }, []);

  // ── Cycle scheduler (timing-based, never blocks on player) ──
  useEffect(() => {
    const token = { cancelled: false };
    const timeouts = [];

    const wait = (ms) =>
      new Promise((resolve) => {
        const t = setTimeout(resolve, ms);
        timeouts.push(t);
      });

    // Fire-and-forget player op — NEVER awaited inside the cycle.
    const playerCall = (fn) => {
      const p = playerRef.current;
      if (!p) return;
      try {
        const r = fn(p);
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch {}
    };

    const runCycle = async () => {
      while (!token.cancelled) {
        // ── INTRO PHASE ──────────────────────────────────────
        setPhase("intro");
        setVideoVisible(false);
        playerCall((p) => p.pause());
        playerCall((p) => p.setCurrentTime(0));

        for (let i = 0; i < WORDS.length; i++) {
          if (token.cancelled) return;
          setWordIdx(i);
          await wait(WORDS[i].hold);
        }
        if (token.cancelled) return;

        // small breath after LOVE!!
        await wait(220);
        if (token.cancelled) return;

        // ── VIDEO REVEAL ─────────────────────────────────────
        setPhase("video");
        playerCall((p) => p.play());
        setVideoVisible(true);

        // 2 full loops
        await wait(durationRef.current * 2 * 1000);
        if (token.cancelled) return;

        // ── VIDEO FADE-OUT ──────────────────────────────────
        setVideoVisible(false);
        await wait(VIDEO_FADE_MS);
        // back to top of loop
      }
    };

    runCycle();

    return () => {
      token.cancelled = true;
      timeouts.forEach(clearTimeout);
      playerCall((p) => p.pause());
    };
  }, []);

  return (
    <>
      {/* Full-bleed WHITE backdrop — always sits above the iframe but below
          the intro overlay / content. It stays in place until the video is
          actually ready to paint, guaranteeing the user never sees a
          loading poster or partial frame. */}
      <div
        aria-hidden
        data-testid="hero-video-backdrop"
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background: "#ffffff",
          opacity: videoReady && videoVisible ? 0 : 1,
          transition: `opacity ${VIDEO_FADE_MS}ms cubic-bezier(0.4,0,0.2,1)`,
          willChange: "opacity",
        }}
      />

      {/* Background video — full hero cover, animated opacity for smooth fades.
          `videoReady` gate ensures we never reveal before a real frame has
          been painted. */}
      <motion.div
        aria-hidden
        className="absolute inset-0 z-0 overflow-hidden pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: videoReady && videoVisible ? 1 : 0 }}
        transition={{ duration: VIDEO_FADE_MS / 1000, ease: [0.4, 0, 0.2, 1] }}
        style={{ willChange: "opacity" }}
      >
        <iframe
          ref={iframeRef}
          title="Love Exclusive — ambient"
          src={VIMEO_SRC}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          data-testid="hero-bg-video"
          className="hero-video-frame absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: "max(100%, calc(100vh * 16 / 9))",
            height: "max(100%, calc(100vw * 9 / 16))",
            minWidth: "100%",
            minHeight: "100%",
            pointerEvents: "none",
          }}
        />
      </motion.div>

      {/* INTRO TEXT OVERLAY — DO → IT → WITH → LOVE.
          Full-bleed white so nothing can leak through (even if the fixed
          header hasn't rendered yet). z-[30] keeps it above the video and
          backdrop but below the header (z-50). */}
      <AnimatePresence>
        {phase === "intro" && (
          <motion.div
            data-testid="hero-intro-overlay"
            key="intro-overlay"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0 z-[30] flex items-center justify-center pointer-events-none px-6"
            style={{ background: "#ffffff", willChange: "opacity" }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={`${wordIdx}-${WORDS[wordIdx].text}`}
                data-testid={`hero-intro-word-${WORDS[wordIdx].text}`}
                initial={{ opacity: 0, scale: 0.92, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.05, y: -10 }}
                transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-black uppercase leading-none whitespace-nowrap select-none text-[clamp(3.5rem,16vw,15rem)] text-black"
                style={{ letterSpacing: "-0.005em" }}
              >
                {WORDS[wordIdx].text}
              </motion.span>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
